import React, { useMemo } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

marked.setOptions({
  breaks: true,
  gfm: true,
});

const PreviewContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid rgba(0, 102, 255, 0.12);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 8px 24px rgba(0, 102, 255, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: rgba(0, 102, 255, 0.2);
    box-shadow: 
      0 1px 3px rgba(0, 0, 0, 0.08),
      0 12px 32px rgba(0, 102, 255, 0.12);
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(0, 102, 255, 0.08);
  background: linear-gradient(90deg, rgba(0, 102, 255, 0.02) 0%, transparent 100%);
`;

const PreviewTitle = styled.h3`
  margin: 0;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(0, 102, 255, 0.8);
`;

const MetricsContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const MetricLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.4);
`;

const MetricValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: rgba(0, 102, 255, 0.9);
  font-family: 'Monaco', 'Menlo', monospace;
`;

const PreviewContent = styled.div`
  padding: 0.4rem 1.5rem 1.5rem 1.5rem;
  min-height: 200px;
  max-height: 500px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 102, 255, 0.03);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 102, 255, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(0, 102, 255, 0.3);
    }
  }

  /* Markdown rendering styles */
  h1, h2, h3, h4, h5, h6 {
    margin: 1.25rem 0 0.75rem 0;
    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.9);
    
    &:first-child {
      margin-top: 0;
    }
  }

  h1 { font-size: 24px; }
  h2 { font-size: 20px; }
  h3 { font-size: 17px; }
  h4 { font-size: 15px; }

  p {
    margin: 0.875rem 0;
    line-height: 1.65;
    color: rgba(0, 0, 0, 0.75);
    font-size: 14px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  ul, ol {
    margin: 0.875rem 0;
    padding-left: 1.75rem;
    color: rgba(0, 0, 0, 0.75);
    line-height: 1.75;
    
    li {
      margin: 0.5rem 0;
      font-size: 14px;
    }
  }

  code {
    background: rgba(0, 102, 255, 0.08);
    color: rgba(0, 102, 255, 0.9);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 13px;
    font-weight: 500;
  }

  pre {
    background: rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    padding: 1rem;
    overflow-x: auto;
    margin: 1rem 0;
    
    code {
      background: none;
      color: rgba(0, 0, 0, 0.8);
      padding: 0;
      border-radius: 0;
    }
  }

  blockquote {
    margin: 1rem 0;
    padding-left: 1rem;
    border-left: 3px solid rgba(0, 102, 255, 0.3);
    color: rgba(0, 0, 0, 0.65);
    font-style: italic;
    font-size: 14px;
  }

  strong {
    font-weight: 700;
    color: rgba(0, 0, 0, 0.85);
  }

  em {
    font-style: italic;
    color: rgba(0, 0, 0, 0.8);
  }

  a {
    color: rgba(0, 102, 255, 0.85);
    text-decoration: none;
    border-bottom: 1px solid rgba(0, 102, 255, 0.3);
    transition: all 0.2s ease;
    
    &:hover {
      color: rgba(0, 102, 255, 1);
      border-bottom-color: rgba(0, 102, 255, 0.6);
    }
  }

  hr {
    margin: 1.5rem 0;
    border: none;
    border-top: 1px solid rgba(0, 102, 255, 0.1);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    font-size: 14px;
    
    th, td {
      padding: 0.75rem;
      text-align: left;
      border-bottom: 1px solid rgba(0, 102, 255, 0.1);
    }
    
    th {
      background: rgba(0, 102, 255, 0.05);
      font-weight: 600;
      color: rgba(0, 102, 255, 0.9);
    }
    
    tr:hover {
      background: rgba(0, 102, 255, 0.03);
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(0, 0, 0, 0.35);
  font-size: 14px;
  font-style: italic;
`;

export const PromptPreview = ({ systemPromptDraft }) => {
  const stats = useMemo(() => {
    if (!systemPromptDraft) {
      return { words: 0, chars: 0, lines: 0 };
    }
    
    const chars = systemPromptDraft.length;
    const words = systemPromptDraft.trim().split(/\s+/).length;
    const lines = systemPromptDraft.split('\n').length;
    
    return { words, chars, lines };
  }, [systemPromptDraft]);

  const sanitizedHtml = useMemo(() => {
    if (!systemPromptDraft) return '';
    return DOMPurify.sanitize(marked.parse(systemPromptDraft));
  }, [systemPromptDraft]);

  return (
    <PreviewContainer>
      <PreviewHeader>
        <PreviewTitle>📋 Prompt Preview</PreviewTitle>
        <MetricsContainer>
            <Metric>
            <MetricLabel>Tokens</MetricLabel>
            <MetricValue>{Math.round(systemPromptDraft.length / 4)}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>Words</MetricLabel>
            <MetricValue>{stats.words}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>Chars</MetricLabel>
            <MetricValue>{stats.chars}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>Lines</MetricLabel>
            <MetricValue>{stats.lines}</MetricValue>
          </Metric>
        </MetricsContainer>
      </PreviewHeader>

      <PreviewContent>
        {systemPromptDraft ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        ) : (
          <EmptyState>No prompt content yet...</EmptyState>
        )}
      </PreviewContent>
    </PreviewContainer>
  );
};

export default PromptPreview;